import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { createHubConnection } from "@/utils/signalr";
import { HubType } from "@/services/negotiateService";
import { useServices } from "@/hooks/useServices";
import { useQueryClient } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { invalidateMissingDocumentHtml } from "@/lib/documentHtmlQuery";
import {
  UploadItem,
  UploadItemStatus,
} from "@/components/upload/types/upload-item";
import { UploadHubEvent, IndexingStatusPayload } from "../types/upload.types";
import { logger } from "@/lib/logger";
import {
  applyIndexingStatusMessage,
  applyUploadStatusSnapshot,
} from "../utils/uploadIndexingStatus";

type EventHandlers = {
  uploadStatus?: (payload: IndexingStatusPayload) => void;
  workspaceUpdated?: () => void;
  reconnecting?: () => void;
  reconnected?: (connectionId?: string) => Promise<void>;
  close?: (error?: Error) => void;
};

type UseUploadHubConnectionParams = {
  workspaceId: string | null;
  locationPathname: string;
  uploadsByWorkspaceRef: React.MutableRefObject<Map<string, UploadItem[]>>;
  updateUploadsForWorkspace: (
    targetWorkspaceId: string,
    updater: (prev: UploadItem[]) => UploadItem[]
  ) => void;
};

type UseUploadHubConnectionReturn = {
  hubConnection: HubConnection | null;
  currentWorkspaceIdRef: React.MutableRefObject<string | null>;
};

/**
 * Hook to manage SignalR hub connection for upload status updates
 */
export const useUploadHubConnection = ({
  workspaceId,
  locationPathname,
  uploadsByWorkspaceRef,
  updateUploadsForWorkspace,
}: UseUploadHubConnectionParams): UseUploadHubConnectionReturn => {
  const services = useServices();
  const { negotiate, setContext } = services.items.negotiate;
  const workspaceService = services.items.workspace;
  const queryClient = useQueryClient();

  // State
  const [hubConnection, setHubConnection] = useState<HubConnection | null>(
    null
  );

  // Refs
  const connectionInitialized = useRef(false);
  const currentWorkspaceIdRef = useRef<string | null>(null);
  const hubConnectionRef = useRef<HubConnection | null>(null);
  const isStartingRef = useRef(false);
  const cleanupInProgressRef = useRef(false);
  const eventHandlersRef = useRef<EventHandlers>({});

  // Keep ref in sync with state
  useEffect(() => {
    hubConnectionRef.current = hubConnection;
  }, [hubConnection]);

  // Fetch upload status snapshot from backend to sync missed events
  const fetchUploadStatusSnapshot = useCallback(
    async (targetWorkspaceId: string) => {
      try {
        const currentUploads =
          uploadsByWorkspaceRef.current.get(targetWorkspaceId) || [];

        // Only fetch if there are uploads to check
        if (currentUploads.length === 0) {
          return;
        }

        // Get all blob names from current uploads
        const blobNames = currentUploads.map((upload) => upload.id);

        // Use workspaceService to fetch status snapshot
        const response = await workspaceService.getUploadStatus(
          targetWorkspaceId,
          blobNames
        );

        if (!response.success) {
          logger.warn("Failed to fetch the upload status snapshot", {
            operation: "fetchUploadStatusSnapshot",
            workspaceId: targetWorkspaceId,
            status: response.status,
          });
          return;
        }

        const data = response.value;

        // Update uploads based on snapshot
        // Only update items that are not in final states (Completed/Failed)
        updateUploadsForWorkspace(targetWorkspaceId, (prev) =>
          prev.map((upload) => {
            // Skip items that are already in final states
            if (
              upload.status === UploadItemStatus.Completed ||
              upload.status === UploadItemStatus.Failed
            ) {
              return upload;
            }

            const statusInfo = data.statuses?.[upload.id];
            if (!statusInfo) {
              // No status info - keep current state (might still be processing)
              return upload;
            }

            return applyUploadStatusSnapshot(upload, statusInfo);
          })
        );

        // Invalidate workspace queries to trigger refetch
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.workspace],
          exact: false,
        });
        // Documents opened before their HTML existed can load now.
        invalidateMissingDocumentHtml(queryClient);

        // Invalidate search queries so new documents appear in global search
        queryClient.invalidateQueries({
          queryKey: [PATH_KEYS.search, "tenant"],
          exact: false,
        });
      } catch (error) {
        logger.warn("Failed to fetch the upload status snapshot", {
          operation: "fetchUploadStatusSnapshot",
          error,
          workspaceId: targetWorkspaceId,
        });
        // Don't throw - this is a nice-to-have sync, not critical
      }
    },
    [
      uploadsByWorkspaceRef,
      updateUploadsForWorkspace,
      workspaceService,
      queryClient,
    ]
  );

  useEffect(() => {
    // Don't start connection if no workspaceId
    if (!workspaceId) {
      return;
    }

    let isMounted = true;

    // Cleanup function to stop existing connection when workspaceId changes
    const cleanup = async () => {
      if (cleanupInProgressRef.current) {
        return; // Already cleaning up
      }

      cleanupInProgressRef.current = true;
      const connection = hubConnectionRef.current;

      if (connection) {
        try {
          // Remove all event handlers before stopping
          const handlers = eventHandlersRef.current;
          if (handlers.uploadStatus) {
            connection.off(
              UploadHubEvent.SendIndexingStatusMessage,
              handlers.uploadStatus
            );
          }
          if (handlers.workspaceUpdated) {
            connection.off(
              UploadHubEvent.WorkspaceUpdated,
              handlers.workspaceUpdated
            );
          }
          // Note: SignalR doesn't provide a way to remove lifecycle handlers,
          // but they will be cleaned up when connection.stop() is called
          eventHandlersRef.current = {};

          connectionInitialized.current = false;
          await connection.stop();
        } catch (err) {
          logger.warn("Failed to stop the upload hub connection", {
            operation: "stopUploadHubConnection",
            error: err,
            workspaceId,
          });
        } finally {
          if (isMounted) {
            setHubConnection(null);
            hubConnectionRef.current = null;
          }
          cleanupInProgressRef.current = false;
        }
      } else {
        cleanupInProgressRef.current = false;
      }
    };

    const startConnection = async () => {
      // Prevent multiple simultaneous start attempts
      if (isStartingRef.current) {
        return;
      }

      const existingConnection = hubConnectionRef.current;

      // Validate that we don't already have an active connection for this workspace
      if (
        existingConnection &&
        (existingConnection.state === HubConnectionState.Connected ||
          existingConnection.state === HubConnectionState.Connecting) &&
        currentWorkspaceIdRef.current === workspaceId
      ) {
        return; // Already connected to this workspace
      }

      // If workspaceId changed, cleanup existing connection first
      if (
        currentWorkspaceIdRef.current !== null &&
        currentWorkspaceIdRef.current !== workspaceId
      ) {
        await cleanup();
        // Wait a bit to ensure cleanup is complete
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Prevent multiple initialization attempts for the same workspace
      if (
        connectionInitialized.current &&
        currentWorkspaceIdRef.current === workspaceId
      ) {
        return;
      }

      isStartingRef.current = true;
      connectionInitialized.current = true;
      currentWorkspaceIdRef.current = workspaceId;

      try {
        // Double-check after async cleanup
        if (!isMounted || currentWorkspaceIdRef.current !== workspaceId) {
          isStartingRef.current = false;
          return;
        }

        const connection = await createHubConnection(
          HubType.UploadHub,
          negotiate
        );

        // Check again before starting
        if (!isMounted || currentWorkspaceIdRef.current !== workspaceId) {
          await connection.stop();
          isStartingRef.current = false;
          return;
        }

        await connection.start();

        // Final check before setting up handlers
        if (!isMounted || currentWorkspaceIdRef.current !== workspaceId) {
          await connection.stop();
          isStartingRef.current = false;
          return;
        }

        await setContext({
          sessionId: workspaceId,
          connectionId: connection.connectionId,
          hub: HubType.UploadHub,
        });

        // Fetch upload status snapshot after connecting to sync any missed events
        await fetchUploadStatusSnapshot(workspaceId);

        // Store handler references for cleanup
        const uploadStatusHandler = (payload: IndexingStatusPayload) => {
          const handlerWorkspaceId = currentWorkspaceIdRef.current;
          if (!isMounted || !handlerWorkspaceId) return;

          // Update uploads for the workspace that received this message
          updateUploadsForWorkspace(handlerWorkspaceId, (prev) =>
            prev.map((upload) => {
              if (upload.id !== payload.documentBlobName) {
                return upload;
              }

              return applyIndexingStatusMessage(upload, payload);
            })
          );
        };

        const reconnectingHandler = () => {
          if (!isMounted || currentWorkspaceIdRef.current !== workspaceId)
            return;
        };

        const reconnectedHandler = async (connectionId?: string) => {
          if (!connectionId || !isMounted) return;
          if (currentWorkspaceIdRef.current !== workspaceId) return;

          await setContext({
            sessionId: workspaceId,
            connectionId: connectionId,
            hub: HubType.UploadHub,
          });
        };

        const workspaceUpdatedHandler = () => {
          if (!isMounted || currentWorkspaceIdRef.current !== workspaceId) {
            return;
          }

          // Invalidate workspace queries to trigger refetch
          queryClient.invalidateQueries({
            queryKey: [PATH_KEYS.workspace],
            exact: false,
          });
          // Documents opened before their HTML existed can load now.
          invalidateMissingDocumentHtml(queryClient);

          // Invalidate search queries so new documents appear in global search
          queryClient.invalidateQueries({
            queryKey: [PATH_KEYS.search, "tenant"],
            exact: false,
          });
        };

        const closeHandler = (error?: Error) => {
          if (error) {
            logger.warn("Upload hub connection closed with an error", {
              operation: "uploadHubConnection",
              error,
              workspaceId,
            });
          }
          if (isMounted && currentWorkspaceIdRef.current === workspaceId) {
            connectionInitialized.current = false;
            currentWorkspaceIdRef.current = null;
            setHubConnection(null);
            hubConnectionRef.current = null;
            eventHandlersRef.current = {};
          }
        };

        // Register handlers
        connection.on(
          UploadHubEvent.SendIndexingStatusMessage,
          uploadStatusHandler
        );
        connection.on(UploadHubEvent.WorkspaceUpdated, workspaceUpdatedHandler);
        connection.onreconnecting(reconnectingHandler);
        connection.onreconnected(reconnectedHandler);
        connection.onclose(closeHandler);

        // Store references for cleanup
        eventHandlersRef.current = {
          uploadStatus: uploadStatusHandler,
          workspaceUpdated: workspaceUpdatedHandler,
          reconnecting: reconnectingHandler,
          reconnected: reconnectedHandler,
          close: closeHandler,
        };

        if (isMounted && currentWorkspaceIdRef.current === workspaceId) {
          setHubConnection(connection);
          hubConnectionRef.current = connection;
        } else {
          await connection.stop();
        }
      } catch (err) {
        logger.error("Failed to start the upload hub connection", {
          operation: "startUploadHubConnection",
          error: err,
          workspaceId,
        });
        if (isMounted) {
          connectionInitialized.current = false;
          currentWorkspaceIdRef.current = null;
        }
      } finally {
        isStartingRef.current = false;
      }
    };

    startConnection();

    // Cleanup on unmount or workspaceId change
    return () => {
      isMounted = false;
      const connection = hubConnectionRef.current;
      const savedWorkspaceId = currentWorkspaceIdRef.current;

      // Always cleanup if we have a connection for this workspace
      if (savedWorkspaceId === workspaceId && connection) {
        // Fire and forget cleanup - we can't await in cleanup
        cleanup().catch((err) => {
          logger.warn("Failed to clean up the upload hub connection", {
            operation: "stopUploadHubConnection",
            error: err,
            workspaceId,
          });
        });
      }

      // Reset initialization flag when unmounting
      if (savedWorkspaceId === workspaceId) {
        connectionInitialized.current = false;
        currentWorkspaceIdRef.current = null;
        isStartingRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, locationPathname]);

  return {
    hubConnection,
    currentWorkspaceIdRef,
  };
};
